"use client";

import React, { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import BookingBottomSheet from "@/src/features/listing/components/BookingBottomSheet";
import ConfirmAndPayPage from "@/src/features/listing/components/ConfirmPay/ConfirmAndPayPage";
import { confirmPayService } from "@/src/features/listing/confirmPay";
import {
  type IFilter,
  BookingDateModalMobile,
  BookingGuestModalMobile,
} from "@/src/shared/features/search";
import type { IListing } from "@/src/shared/types/listing";
import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import { useSnackbar } from "notistack";
import { dateAdd, formatYearDate } from "@/src/shared/utils/timeFunctionsModel";
import { instantBookingModel } from "@/src/shared/features/booking";
import { buildUrl } from "@/src/features/listing/buildUrl";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Loader2 } from "lucide-react";
import PriceDisplay from "@/src/features/listing/components/PriceDisplay";
import GuestFavouriteStayBanner from "@/src/features/listing/components/GuestFavouriteStayBanner";
import { listingService } from "@/src/features/listing/services";
import {
  type BookingFormValues,
  type IBookingFormType,
  bookingFormSubmitSchema,
} from "@/src/shared/types/schemaType";
import { getFormattedDateRange } from "@/src/shared/utils/global";
import { useBookingRequest } from "@/src/features/listing/hooks/useBookingRequest";
import { useAuth } from "@/src/core/auth/useAuth";
import useAnalytics from "@/src/lib/useAnalytics";
import type { BlockedDate } from "@/src/shared/types/blockedDate";

interface IFixedBottomReserveBarProps {
  setFilters: React.Dispatch<React.SetStateAction<IFilter>>;
  listing: IListing;
  filters: IFilter;
  className?: string;
  hide?: boolean;
  handleUpdateQuery: () => void;
  blockedDates: BlockedDate[];
  checkoutOnlyDates?: string[]; // newly added
  /** Bumped by the page to open the booking sheet (the contact screen's "Book"). */
  openRequest?: number;
}

const FixedBottomReserveBar: React.FC<IFixedBottomReserveBarProps> = ({
  filters,
  setFilters,
  listing,
  className = "",
  handleUpdateQuery,
  hide = false,
  blockedDates,
  checkoutOnlyDates = [],
  openRequest = 0,
}) => {
  const router = useAppRouter();
  const requestBooking = useBookingRequest();
  const [isBookingModalOpen, setIsBookingModalOpen] = useState<boolean>(false);
  const [stayStep, setStayStep] = useState<"dates" | "guests" | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pickedStay, setPickedStay] = useState<{
    checkIn: Date | null;
    checkOut: Date | null;
  }>({ checkIn: null, checkOut: null });
  const [isQuerySyncPending, setIsQuerySyncPending] = useState(false);
  const [isBookingLoading, setIsLoading] = useState<boolean>(false);
  const { location, placeName, message } = router.query;

  const { enqueueSnackbar } = useSnackbar();
  const { requireAuth, user_profile } = useAuth();
  const analytics = useAnalytics();
  const isInstantBooking = instantBookingModel(listing);
  const isGuestFavourite = !!listingService.guestFavouriteBadge(listing.badges);
  const [isPendingEvent, setPendingEvent] = useState<boolean>(false);

  const methods = useForm<BookingFormValues>({
    resolver: yupResolver(bookingFormSubmitSchema, {
      context: { $isMessageRequired: !isInstantBooking },
    }),
    reValidateMode: "onChange",
    defaultValues: {
      message: (message as string) ?? "",
      guest: String(filters?.guests?.adults || 1),
      dates: getFormattedDateRange(filters?.checkIn, filters?.checkOut),
    },
  });

  const {
    handleSubmit,
    trigger,
    setError,
    setValue,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (user_profile && isPendingEvent) {
      openBookingStep();
      setPendingEvent(false);
    }
  }, [user_profile, isPendingEvent]);

  // Update only specific fields when filters change to preserve textarea content
  useEffect(() => {
    setValue("guest", String(filters?.guests?.adults || 1));
    setValue(
      "dates",
      getFormattedDateRange(filters?.checkIn, filters?.checkOut),
    );
    // Don't reset the message field to preserve user input
  }, [filters?.guests?.adults, filters?.checkIn, filters?.checkOut, setValue]);

  // Separate effect to handle initial message from query params
  useEffect(() => {
    if (message) {
      setValue("message", message as string);
    }
  }, [message, setValue]);

  function openBookingStep() {
    if (!isInstantBooking) {
      setIsBookingModalOpen(true);
      return;
    }
    setFilters((prev) => ({
      ...prev,
      guests: confirmPayService.fitGuests(prev.guests, {
        adults: listing.max_guest,
        children: listing.max_child,
        infants: listing.max_infant,
      }),
    }));
    setIsConfirmOpen(true);
  }

  const handleBookingModalOpen = () => {
    requireAuth(
      () => {
        openBookingStep();
      },
      () => {
        // This callback runs when login modal opens (user not authenticated)
        // Store the booking data to submit after login
        setPendingEvent(true);
      },
    );
  };
  useEffect(() => {
    if (openRequest > 0) handleBookingModalOpen();
    // Only a new request opens the sheet.
  }, [openRequest]);

  const handleBookingModalClose = () => {
    setIsBookingModalOpen(false);
  };

  const hasStayDates = Boolean(filters.checkIn && filters.checkOut);

  const handleReserveClick = () => {
    if (hasStayDates) handleBookingModalOpen();
    else setStayStep("dates");
  };

  const handleStayDatesDone = (dates: {
    checkIn: Date | null;
    checkOut: Date | null;
  }) => {
    if (!dates.checkIn) {
      setStayStep(null);
      return;
    }
    if (!dates.checkOut) {
      enqueueSnackbar("Please select a check-out date", { variant: "error" });
      return;
    }
    setPickedStay(dates);
    setStayStep("guests");
  };

  const handleStayGuestsDone = (stayGuests: IFilter["guests"]) => {
    setFilters((prev) => ({ ...prev, ...pickedStay, guests: stayGuests }));
    setStayStep(null);
    setIsQuerySyncPending(true);
    handleBookingModalOpen();
  };

  useEffect(() => {
    if (!isQuerySyncPending) return;
    setIsQuerySyncPending(false);
    handleUpdateQuery();
  }, [filters, isQuerySyncPending, handleUpdateQuery]);

  const updateGuestCount = (
    type: keyof IFilter["guests"],
    op: "add" | "subtract",
  ) =>
    setFilters((prev) => ({
      ...prev,
      guests: {
        ...prev.guests,
        [type]: Math.max(0, (prev.guests[type] ?? 0) + (op === "add" ? 1 : -1)),
      },
    }));

  const onSubmit = async (data: IBookingFormType) => {
    if (!isInstantBooking && (!data.message || data.message.trim() === "")) {
      setError(
        "message",
        { message: "Please write something" },
        { shouldFocus: true },
      );
      return;
    }
    const isValid = await trigger();
    if (!isValid) {
      return;
    }
    setIsLoading(true);
    const fromDate = filters.checkIn ? formatYearDate(filters.checkIn) : null;
    const toDate = filters.checkOut
      ? formatYearDate(filters.checkOut)
      : fromDate
        ? formatYearDate(dateAdd(fromDate, 1))
        : null;

    if (user_profile) {
      if (fromDate && toDate) {
        const payload = {
          message: data.message,
          from: fromDate,
          to: toDate,
          listing_id: listing?.id,
          guests: Number(filters.guests.adults),
          child: Number(filters.guests.children),
          infant: Number(filters.guests.infants),
          os_platform: 3,
        };

        try {
          // Track add to cart with proper error handling
          analytics.add_to_cart({
            item: listing,
            value: listing.price,
            currency: "BDT",
            params: {
              booking_dates: `${payload.from} to ${payload.to}`,
              guests:
                payload.guests + (payload.child || 0) + (payload.infant || 0),
            },
          });
        } catch (_error) {}

        await requestBooking(payload, {
          hostId: listing.host.id,
          guestId: user_profile.id,
          instant: isInstantBooking,
        });
        setIsLoading(false);
      } else {
        enqueueSnackbar("Please select check-in and check-out dates", {
          variant: "error",
        });
      }
    } else {
      const params = {
        location,
        placeName,
        guest: filters.guests.adults,
        child: filters.guests.children,
        infant: filters.guests.infants,
        message: data.message,
        from: fromDate,
        to: toDate,
      };
      const fullUrl = buildUrl(
        window.location.origin + window.location.pathname,
        params,
      );
      localStorage.setItem("redirectUrl", fullUrl);
      void router.replace(`/signin?next=${encodeURIComponent(fullUrl)}`);
    }

    setIsBookingModalOpen(false);
  };

  return (
    <>
      {/* The app's booking bar: price on the left, a pink pill on the right. */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 block border-t border-[#EBEBEB] bg-white md:hidden ${className} ${hide ? "!hidden" : ""}`}
      >
        {/* The app's Guest Favourite stay banner, on the bar's top edge. */}
        {isGuestFavourite && <GuestFavouriteStayBanner />}
        <div className="flex items-center justify-between gap-3 px-6 pb-[max(16px,env(safe-area-inset-bottom))] pt-3.5">
          <PriceDisplay filters={filters} variant="mobile" />
          <button
            type="button"
            onClick={handleReserveClick}
            disabled={isSubmitting || isBookingLoading}
            className="flex h-[50px] shrink-0 items-center justify-center gap-2 rounded-full bg-[#DF146D] px-6 text-base font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-70"
          >
            {(isSubmitting || isBookingLoading) && (
              <Loader2 size={18} className="animate-spin" />
            )}
            {!hasStayDates
              ? "Select date"
              : isInstantBooking
                ? "Book Now"
                : "Request to Book"}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {stayStep && (
          <BookingDateModalMobile
            key="stay-dates"
            appearance="app"
            blockedDates={blockedDates}
            checkoutOnlyDates={checkoutOnlyDates}
            filters={filters}
            setFilters={setFilters}
            onBack={() => setStayStep(null)}
            onDoneDates={handleStayDatesDone}
            doneLabel="Next"
          />
        )}
        {stayStep === "guests" && (
          <BookingGuestModalMobile
            key="stay-guests"
            appearance="app"
            listing={listing}
            filters={filters}
            updateGuestCount={updateGuestCount}
            onBack={() => setStayStep("dates")}
            onDoneGuests={handleStayGuestsDone}
            doneLabel="Next"
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isConfirmOpen && (
          <ConfirmAndPayPage
            key="confirm-and-pay"
            listing={listing}
            filters={filters}
            setFilters={setFilters}
            blockedDates={blockedDates}
            checkoutOnlyDates={checkoutOnlyDates}
            onStayChanged={() => setIsQuerySyncPending(true)}
            onClose={() => setIsConfirmOpen(false)}
          />
        )}
      </AnimatePresence>

      <BookingBottomSheet
        filters={filters}
        onClose={handleBookingModalClose}
        singleListingData={listing}
        onSubmit={handleSubmit(onSubmit)}
        method={methods}
        isSubmitting={isBookingLoading}
        open={isBookingModalOpen}
        setFilters={setFilters}
        forContactHost={false}
        buttonText={isInstantBooking ? "Book Now" : "Send Booking Request"}
        handleUpdateQuery={handleUpdateQuery}
        blockedDates={blockedDates}
        checkoutOnlyDates={checkoutOnlyDates}
      />
    </>
  );
};

export default FixedBottomReserveBar;
